import { ref, set, get } from 'firebase/database';
import { database } from '../firebase';

export interface ReferralData {
  referringUserId: string;
  newUserId: string;
  signupMethod: 'email' | 'google' | 'apple' | 'phone';
  timestamp: number;
  userAgent?: string;
  ipAddress?: string;
}

export class ReferralLoggingService {
  // Log a new user signup with referral data
  async logReferralSignup(referralData: ReferralData): Promise<void> {
    try {
      const referralLogRef = ref(database, `referral_logs/${referralData.newUserId}`);
      
      await set(referralLogRef, {
        ...referralData,
        createdAt: Date.now()
      });

      // Also update the referring user's referral statistics
      await this.updateReferralStats(referralData.referringUserId);
      
      console.log('Referral signup logged successfully:', referralData);
    } catch (error) {
      console.error('Error logging referral signup:', error);
      throw error;
    }
  }

  // Update referral statistics for the referring user
  private async updateReferralStats(referringUserId: string): Promise<void> {
    try {
      const statsRef = ref(database, `referral_stats/${referringUserId}`);
      const snapshot = await get(statsRef);
      
      const currentStats = snapshot.exists() ? snapshot.val() : {
        totalReferrals: 0,
        successfulSignups: 0,
        lastReferralDate: null
      };

      const updatedStats = {
        ...currentStats,
        totalReferrals: currentStats.totalReferrals + 1,
        successfulSignups: currentStats.successfulSignups + 1,
        lastReferralDate: Date.now()
      };

      await set(statsRef, updatedStats);
    } catch (error) {
      console.error('Error updating referral stats:', error);
      // Don't throw here as this is supplementary data
    }
  }

  // Get referral statistics for a user
  async getReferralStats(userId: string) {
    try {
      const statsRef = ref(database, `referral_stats/${userId}`);
      const snapshot = await get(statsRef);
      
      return snapshot.exists() ? snapshot.val() : {
        totalReferrals: 0,
        successfulSignups: 0,
        lastReferralDate: null
      };
    } catch (error) {
      console.error('Error getting referral stats:', error);
      return {
        totalReferrals: 0,
        successfulSignups: 0,
        lastReferralDate: null
      };
    }
  }

  // Get all referral logs for a user (as the referring user)
  async getReferralLogs(referringUserId: string) {
    try {
      const logsRef = ref(database, 'referral_logs');
      const snapshot = await get(logsRef);
      
      if (!snapshot.exists()) {
        return [];
      }

      const allLogs = snapshot.val();
      return Object.entries(allLogs)
        .filter(([_, log]: [string, any]) => log.referringUserId === referringUserId)
        .map(([newUserId, log]: [string, any]) => ({
          newUserId,
          signupMethod: log.signupMethod,
          timestamp: log.timestamp,
          createdAt: log.createdAt
        }))
        .sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error('Error getting referral logs:', error);
      return [];
    }
  }

  // Check if a user was referred by someone
  async getReferralSource(newUserId: string) {
    try {
      const logRef = ref(database, `referral_logs/${newUserId}`);
      const snapshot = await get(logRef);
      
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Error getting referral source:', error);
      return null;
    }
  }
}

export const referralLoggingService = new ReferralLoggingService();
